import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  startDeviceEnroll,
  deleteDeviceBiometric,
  getDeviceCommandLogs,
  getDeviceBiometrics,
  cancelDeviceCommands,
} from "../../pages/Apihelper/ApiHelperUser";

const POLL_MS = 3000;

const FINGERS = [
  { fid: 0, label: "Left little finger" },
  { fid: 1, label: "Left ring finger" },
  { fid: 2, label: "Left middle finger" },
  { fid: 3, label: "Left index finger" },
  { fid: 4, label: "Left thumb" },
  { fid: 5, label: "Right thumb" },
  { fid: 6, label: "Right index finger" },
  { fid: 7, label: "Right middle finger" },
  { fid: 8, label: "Right ring finger" },
  { fid: 9, label: "Right little finger" },
];

const STATUS_LABEL = {
  queued: "Waiting for machine",
  dispatched: "Sent to machine",
  success: "Done",
  failed: "Failed",
};
const STATUS_COLOR = {
  queued: "default",
  dispatched: "info",
  success: "success",
  failed: "error",
};

function StepRow({ label, log }) {
  const status = log?.status || "queued";
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2">{label}</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {(status === "queued" || status === "dispatched") && <CircularProgress size={14} />}
          <Chip size="small" label={STATUS_LABEL[status] || status} color={STATUS_COLOR[status] || "default"} />
        </Stack>
      </Stack>
      {status === "failed" && log?.error_message && (
        <Typography variant="caption" color="error">{log.error_message}</Typography>
      )}
    </Stack>
  );
}

/**
 * Manage a person's fingerprints on the machine: enrol (any finger),
 * delete one finger / all fingers / the face. Follows every command's
 * status from the device command logs.
 *
 * Props: open, onClose, token, personType ("employee"|"nonregister"|"member"),
 *        personId, personName, autoStart (enrol the right index finger on open,
 *        used right after adding someone; default false)
 */
export default function DeviceEnrollDialog({ open, onClose, token, personType, personId, personName, autoStart = false }) {
  const [fid, setFid] = useState(6);
  const [steps, setSteps] = useState([]); // [{ key, label, cmdId }]
  const [logs, setLogs] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [onMachine, setOnMachine] = useState({ fingers: [], face: false });
  const startedFor = useRef(null);

  const run = useCallback(async (request, buildSteps) => {
    setBusy(true);
    setError("");
    setLogs({});
    setSteps([]);
    try {
      const res = await request();
      if (String(res.code) === "200") {
        setSteps(buildSteps(res.data));
      } else {
        setError(res.message || "Could not send to the machine");
      }
    } catch (e) {
      setError("Could not reach the server");
    } finally {
      setBusy(false);
    }
  }, []);

  const loadOnMachine = useCallback(async () => {
    if (!personId) return;
    try {
      const res = await getDeviceBiometrics(token, { person_type: personType, person_id: personId });
      if (String(res.code) === "200") {
        setOnMachine({ fingers: (res.data.fingers || []).map(Number), face: !!res.data.face });
      }
    } catch (e) { /* the list is a convenience; ignore */ }
  }, [token, personType, personId]);

  useEffect(() => {
    if (open) loadOnMachine();
  }, [open, loadOnMachine]);

  const cancelPending = async () => {
    try {
      await cancelDeviceCommands(token, steps.map((s) => s.cmdId));
    } finally {
      loadOnMachine();
    }
  };

  const fingerName = (f) => FINGERS.find((x) => x.fid === Number(f))?.label || `finger ${f}`;

  const enrolFinger = (f = fid) =>
    run(
      () => startDeviceEnroll(token, { person_type: personType, person_id: personId, mode: "finger", fid: f }),
      (d) => [
        { key: "user", label: "Add user to machine", cmdId: d.user_cmd_id },
        { key: "enroll", label: `Enrol ${fingerName(f).toLowerCase()} — scan on the machine`, cmdId: d.enroll_cmd_id },
      ]
    );

  const remove = (target, label) => {
    if (!window.confirm(`${label}? This removes it from the machine.`)) return;
    run(
      () => deleteDeviceBiometric(token, { person_type: personType, person_id: personId, target, fid }),
      (d) => [{ key: "delete", label, cmdId: d.cmd_id }]
    );
  };

  // Right after adding someone, start fingerprint enrolment once.
  useEffect(() => {
    if (open && autoStart && personId && startedFor.current !== personId) {
      startedFor.current = personId;
      enrolFinger(6);
    }
  }, [open, autoStart, personId]);

  // Follow the queued commands until they have all finished.
  useEffect(() => {
    if (!open || steps.length === 0) return undefined;
    let stopped = false;
    const ids = steps.map((s) => s.cmdId);
    const tick = async () => {
      try {
        const res = await getDeviceCommandLogs(token, { cmd_ids: ids, limit: 10 });
        if (stopped || !Array.isArray(res.data)) return;
        const byId = {};
        res.data.forEach((l) => { byId[String(l.cmd_id)] = l; });
        setLogs(byId);
      } catch (e) { /* keep polling */ }
    };
    tick();
    const timer = setInterval(tick, POLL_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [open, steps, token]);

  const last = steps.length ? logs[String(steps[steps.length - 1].cmdId)] : null;
  const waiting = steps.length > 0 && !(last && (last.status === "success" || last.status === "failed"));

  // Once the last command has finished, re-read what is on the machine.
  const lastStatus = last?.status;
  useEffect(() => {
    if (lastStatus === "success" || lastStatus === "failed") loadOnMachine();
  }, [lastStatus, loadOnMachine]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Machine biometrics{personName ? ` — ${personName}` : ""}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            size="small"
            label="Finger"
            value={fid}
            onChange={(e) => setFid(Number(e.target.value))}
          >
            {FINGERS.map((f) => (
              <MenuItem key={f.fid} value={f.fid}>
                {f.label}{onMachine.fingers.includes(f.fid) ? "  ✓ on machine" : ""}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {onMachine.fingers.length === 0 && !onMachine.face && (
              <Typography variant="caption" color="text.secondary">Nothing enrolled on the machine yet.</Typography>
            )}
            {onMachine.fingers.map((f) => (
              <Chip key={f} size="small" color="success" variant="outlined" label={fingerName(f)} />
            ))}
            {onMachine.face && <Chip size="small" color="success" variant="outlined" label="Face" />}
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" size="small" disabled={busy || waiting} onClick={() => enrolFinger()}>
              Enrol this finger
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button color="error" variant="outlined" size="small" disabled={busy || waiting}
              onClick={() => remove("finger", `Delete ${fingerName(fid).toLowerCase()}`)}>
              Delete this finger
            </Button>
            <Button color="error" variant="outlined" size="small" disabled={busy || waiting}
              onClick={() => remove("all_fingers", "Delete all fingerprints")}>
              Delete all fingers
            </Button>
            <Button color="error" variant="outlined" size="small" disabled={busy || waiting}
              onClick={() => remove("face", "Delete face")}>
              Delete face
            </Button>
          </Stack>

          {error && <Typography color="error" variant="body2">{error}</Typography>}
          {busy && <Typography variant="body2">Sending to machine…</Typography>}

          {steps.length > 0 && (
            <>
              <Divider />
              {steps.map((s) => (
                <StepRow key={s.key} label={s.label} log={logs[String(s.cmdId)]} />
              ))}
              {waiting && (
                <Typography variant="body2" color="text.secondary">
                  The machine picks this up within a few seconds. Only one enrolment runs at a time.
                </Typography>
              )}
              {waiting && (
                <Button color="warning" variant="outlined" size="small" onClick={cancelPending}>
                  Cancel
                </Button>
              )}
              {last?.status === "success" && (
                <Typography variant="body2" color="success.main">Finished.</Typography>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
